import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircleIcon, ArrowRightIcon, EyeIcon, EyeOffIcon, LockIcon, MailIcon, Loader2Icon } from 'lucide-react';
import { grantAccessSession, verifyAccess } from '../../utils/accessAuth';
interface AccessLoginProps {
  onSuccess: () => void;
}
export function AccessLogin({
  onSuccess
}: AccessLoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }
    setError(null);
    setLoading(true);
    window.setTimeout(() => {
      if (verifyAccess(email, password)) {
        grantAccessSession(remember);
        onSuccess();
      } else {
        setLoading(false);
        setError('That email and password don’t match. Check them and try again.');
      }
    }, 600);
  };
  const fieldBase = 'w-full h-12 rounded-xl bg-white border pl-11 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none shadow-sm transition-[border-color,box-shadow] duration-150 focus:border-vexu focus:ring-4 focus:ring-vexu/15';
  const fieldBorder = error ? 'border-red-400' : 'border-slate-200';
  return <main className="min-h-screen w-full bg-slate-50 flex flex-col" data-id="element-225" data-mp="10bo0owylafsk">
      <div className="flex-1 flex items-center justify-center px-6 py-16" data-id="element-226" data-mp="15362fn8fskel">
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="w-full max-w-[400px]" data-id="element-227" data-mp="1558wqjgmgzp4">
          <div className="flex items-center gap-2.5 mb-10" data-id="element-228" data-mp="1j2nt685am7ql">
            <img src="https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg" alt="" className="w-8 h-8 object-contain" data-id="element-229" data-mp="1j370kuxbhdqd" />
            <span className="text-lg font-semibold tracking-tight text-slate-900" data-id="element-230" data-mp="3b6bcemlldcrr">Vexu.ai</span>
          </div>

          <h1 className="text-[28px] leading-tight font-bold tracking-tight text-slate-900" data-id="element-231" data-mp="3oxy8albmgja0">
            Sign in
          </h1>
          <p className="mt-2 text-[15px] text-slate-500" data-id="element-232" data-mp="2oeukrknqlqo">
            Use your team credentials to open the Vexu app prototype.
          </p>

          <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5" data-id="element-233" data-mp="3tu6jxoqm1ucf">
            <div data-id="element-234" data-mp="bplgjg62sorb">
              <label htmlFor="access-email" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-235" data-mp="bo211rs8uhql">
                Email
              </label>
              <div className="relative" data-id="element-236" data-mp="2dgq708w9vja2">
                <MailIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400" aria-hidden="true" data-id="element-237" data-mp="2dhhpci0uzkie" />
                <input id="access-email" type="email" autoComplete="username" autoFocus placeholder="name@vexu.ai" value={email} onChange={e => {
                setEmail(e.target.value);
                if (error) setError(null);
              }} aria-invalid={Boolean(error)} aria-describedby={error ? 'access-error' : undefined} className={`${fieldBase} ${fieldBorder} pr-4`} data-id="element-238" data-mp="28jq05vrlmchw" />
              </div>
            </div>

            <div data-id="element-239" data-mp="3se2h4hjlyqd2">
              <label htmlFor="access-password" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-240" data-mp="3sekmc3o90ex4">
                Password
              </label>
              <div className="relative" data-id="element-241" data-mp="c2z8ewpn62a">
                <LockIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400" aria-hidden="true" data-id="element-242" data-mp="1qm67jdh03qkx" />
                <input id="access-password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="Enter password" value={password} onChange={e => {
                setPassword(e.target.value);
                if (error) setError(null);
              }} aria-invalid={Boolean(error)} aria-describedby={error ? 'access-error' : undefined} className={`${fieldBase} ${fieldBorder} pr-12`} data-id="element-243" data-mp="1unhpqxxjp9j3" />
                <button type="button" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-vexu" data-id="element-244" data-mp="i6hrcfsb988a">
                  {showPassword ? <EyeOffIcon className="w-[18px] h-[18px]" data-id="element-245" data-mp="2aycyslgqgsmq" /> : <EyeIcon className="w-[18px] h-[18px]" data-id="element-246" data-mp="2exc8ejdetdki" />}
                </button>
              </div>
            </div>

            <AnimatePresence initial={false} data-id="element-247" data-mp="2jy9s50e2ifo4">
              {error && <motion.div id="access-error" role="alert" initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.18,
              ease: [0.23, 1, 0.32, 1]
            }} className="overflow-hidden" data-id="element-248" data-mp="2owl178wjgm5a">
                  <div className="flex items-start gap-2 rounded-xl bg-red-50 px-3.5 py-3 text-sm text-red-700" data-id="element-249" data-mp="25xp5tu8y21pv">
                    <AlertCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-250" data-mp="18grxqridmse9" />
                    <span data-id="element-251" data-mp="1dfur3eozva6e">{error}</span>
                  </div>
                </motion.div>}
            </AnimatePresence>

            <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer select-none w-fit" data-id="element-252" data-mp="15e3w91rknxtb">
              <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-vexu focus:ring-vexu" data-id="element-253" data-mp="11csd9f37eau3" />
              Keep me signed in on this device
            </label>

            <button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-vexu text-white text-[15px] font-semibold flex items-center justify-center gap-2 hover:bg-vexu-600 active:scale-[0.98] transition-[background-color,transform] duration-150 ease-out focus:outline-none focus-visible:ring-4 focus-visible:ring-vexu/30 disabled:opacity-70 disabled:pointer-events-none" data-id="element-254" data-mp="ig0qgvogddla">
              {loading ? <>
                  <Loader2Icon className="w-5 h-5 animate-spin" aria-hidden="true" data-id="element-255" data-mp="3kth1f2g8m6i8" />
                  Signing in…
                </> : <>
                  Sign in
                  <ArrowRightIcon className="w-[18px] h-[18px]" aria-hidden="true" data-id="element-256" data-mp="3bsspcdaemg7z" />
                </>}
            </button>
          </form>

          <p className="mt-8 text-sm text-slate-500" data-id="element-257" data-mp="3grvvy2618ui2">
            Need access? Contact your Vexu.ai administrator.
          </p>
        </motion.div>
      </div>
      <footer className="px-6 pb-6 text-xs text-slate-400 text-center" data-id="element-258" data-mp="cdnm2qxn712r">
        © 2026 Vexu.ai · Confidential prototype
      </footer>
    </main>;
}